/* eslint-disable import/no-unused-modules */
import React, { ComponentProps, useCallback } from "react";

import { InputAdornment } from "@mui/material";
import { Controller, Message, useFormContext } from "react-hook-form";

import TextInput from "../TextInput/TextInput";
import addCommas from "@src/utils/CommaHelperFunctions/addCommas";
import addCommasUnd from "@src/utils/CommaHelperFunctions/addCommasUnd";

import FormFieldLabel from "./Private/FormFieldLabel";

export interface Props {
  name: string;
  label?: React.ReactNode;
  labelTooltip?: string;
  compactTooltip?: boolean;
  placeholder?: string;
  defaultValue?: string;
  disabled?: boolean;
  required?: Message | false;
  rules?: ComponentProps<typeof Controller>["rules"];
  startAdornment?: string;
  endAdornment?: string;
  textAlign?: React.CSSProperties["textAlign"];
  hideErrorMsg?: boolean;
  customRegex?: { regex: RegExp; message: string };
  valueFormatterOnChange?: <T extends string>(input: T) => string;
  valueFormatterOnBlur?: <T extends string>(input: T) => string;
  autoAddCommas?: boolean;
  isFormInputCell?: boolean;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  fullWidth?: boolean;
}

export default function FormNumberField({
  name,
  label,
  labelTooltip,
  compactTooltip,
  placeholder,
  defaultValue,
  disabled,
  required,
  rules,
  startAdornment,
  endAdornment,
  textAlign,
  hideErrorMsg,
  customRegex,
  valueFormatterOnChange,
  valueFormatterOnBlur,
  autoAddCommas,
  isFormInputCell = false,
  fullWidth,
  style,
  inputStyle,
}: Props): JSX.Element {
  const { control } = useFormContext();

  const regex = autoAddCommas ? /^[\d\.,]+$/g : /^[\d\.]+$/g;
  const validationMsg = autoAddCommas
    ? "Must be only numerical digits and commas"
    : "Must be only numerical digits";

  const handleNumberChange = useCallback(
    (fieldOnChange: (value: string) => void) =>
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (autoAddCommas) {
          const newValue = addCommas(event.target.value);
          const existingLength = event.target.value.length;
          const newLength = newValue.length;
          const lengthDiff = newLength - existingLength;
          const caret = event.target.selectionStart;
          const element = event.target;

          // Adjust the cursor position to account for comma additions.
          window.requestAnimationFrame(() => {
            element.selectionStart = caret + lengthDiff;
            element.selectionEnd = caret + lengthDiff;
          });
          fieldOnChange(newValue);
        } else {
          const formattedValue = valueFormatterOnChange
            ? valueFormatterOnChange(event.target.value)
            : event.target.value;
          fieldOnChange(formattedValue);
        }
      },
    [autoAddCommas, valueFormatterOnChange]
  );

  const handleNumberBlur = useCallback(
    (fieldOnChange: (value: string) => void, fieldOnBlur: () => void) =>
      (event: React.FocusEvent<HTMLTextAreaElement>) => {
        if (valueFormatterOnBlur) {
          fieldOnChange(valueFormatterOnBlur(event.target.value));
        }
        fieldOnBlur();
      },
    [valueFormatterOnBlur]
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        pattern: {
          value: customRegex?.regex ?? regex,
          message: customRegex?.message ?? validationMsg,
        },
        required: required,
        ...rules,
      }}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => (
        <TextInput
          fullWidth={fullWidth}
          label={
            !isFormInputCell && label ? (
              <FormFieldLabel
                label={label}
                labelTooltip={labelTooltip}
                compactTooltip={compactTooltip}
              />
            ) : undefined
          }
          placeholder={placeholder}
          onChange={handleNumberChange(field.onChange)}
          value={autoAddCommas ? addCommasUnd(field.value) : field.value}
          name={field.name}
          inputRef={field.ref}
          onBlur={handleNumberBlur(field.onChange, field.onBlur)}
          disabled={disabled}
          error={fieldState.invalid}
          helperText={hideErrorMsg ? undefined : fieldState.error?.message}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            style: { textAlign, ...inputStyle },
          }}
          InputProps={{
            disableUnderline: isFormInputCell,
            ...(startAdornment && {
              startAdornment: (
                <InputAdornment position="start">
                  {startAdornment}
                </InputAdornment>
              ),
            }),
            ...(endAdornment && {
              endAdornment: (
                <InputAdornment position="end">{endAdornment}</InputAdornment>
              ),
            }),
          }}
          variant={isFormInputCell ? "standard" : undefined}
          style={{
            ...(style || {}),
            ...(isFormInputCell && {
              padding: "var(--12pt) var(--16pt)",
              display: "flex",
              alignItems: "center",
              gap: "var(--04pt)",
              background: "transparent",
              border: "none",
            }),
          }}
          sx={{
            ...(isFormInputCell && {
              "& .MuiInput-underline:before": { borderBottom: "none" },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": { borderBottom: "none" },
            }),
          }}
        />
      )}
    />
  );
}
